import { UNASSIGNED_OWNER_ID } from '@paintscout/util'
import type { DropdownSelectOption, GroupedDropdownSelectOption } from '../DropdownSelect'

export interface EstimatorGroupLabels {
  active: string
  selectAllActive: string
  deactivated: string
  selectAllDeactivated: string
  unassigned: string
  unassignedSublabel: string
}

export function buildUnassignedOption(labels: Pick<EstimatorGroupLabels, 'unassigned' | 'unassignedSublabel'>) {
  return { label: labels.unassigned, value: UNASSIGNED_OWNER_ID, sublabel: labels.unassignedSublabel }
}

/**
 * Splits user options into the groups a report/dashboard estimator filter shows:
 * active users (own "Select all active" row, no header), optionally an "Unassigned" row for records
 * with no owner, and a collapsible "Deactivated" group last (left out when empty). Selecting all three
 * matches the unfiltered ("Any estimator") totals.
 *
 * An option counts as deactivated when it carries `inactive: true` (set by EstimatorSelect).
 */
export function buildEstimatorGroups(
  options: DropdownSelectOption[],
  { includeUnassigned, labels }: { includeUnassigned?: boolean; labels: EstimatorGroupLabels }
): GroupedDropdownSelectOption[] {
  const userOptions = options.filter((option) => option.value !== UNASSIGNED_OWNER_ID)
  const active = userOptions.filter((option) => !option.inactive)
  const deactivated = userOptions.filter((option) => option.inactive)

  return [
    { label: labels.active, hideLabel: true, selectAllLabel: labels.selectAllActive, options: active },
    ...(includeUnassigned ? [{ label: labels.unassigned, hideLabel: true, options: [buildUnassignedOption(labels)] }] : []),
    ...(deactivated.length
      ? [
          {
            label: labels.deactivated,
            collapsible: true,
            selectAllLabel: labels.selectAllDeactivated,
            options: deactivated
          }
        ]
      : [])
  ]
}
